import { api } from '../../../api/client';
import type { QuoteDateFilter, QuoteListRow } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Quotes — API calls for the two list screens.
// Backend: `Api/Controllers/V1/Quotes/QuotesController.cs`.
// The shared axios client injects the JWT and drives the global loader, so no
// page here handles auth headers or spinners (§18.6).
// ─────────────────────────────────────────────────────────────────────────────

interface ListEnvelope {
  success: boolean;
  data: QuoteListRow[];
  meta?: { dateFilter?: QuoteDateFilter };
}

export interface OpenQuotesResult {
  rows: QuoteListRow[];
  /** The chip the SERVER actually applied — the page lights this rather than
   *  assuming its own default, so an unrecognised code can't leave the grid and
   *  the chip disagreeing about what is on screen. */
  dateFilter: QuoteDateFilter;
}

/**
 * Open Quotes (nav `acct-quotes-open`) — not cancelled, not expired, Pending
 * Submission or Pending Validation, narrowed by the date chip.
 */
export async function fetchOpenQuotes(dateFilter?: QuoteDateFilter): Promise<OpenQuotesResult> {
  const res = await api.get<ListEnvelope>('/api/v1/quotes/open', {
    params: dateFilter ? { dateFilter } : undefined,
  });
  return {
    rows: res.data.data ?? [],
    dateFilter: res.data.meta?.dateFilter ?? dateFilter ?? '1M',
  };
}

/**
 * Quotes Tracking (nav `acct-quotes-tracking`) — EVERY quote, any status,
 * cancelled and expired included, narrowed only by the date chip (3M when
 * omitted).
 */
export async function fetchQuotesTracking(dateFilter?: QuoteDateFilter): Promise<OpenQuotesResult> {
  const res = await api.get<ListEnvelope>('/api/v1/quotes/tracking', {
    params: dateFilter ? { dateFilter } : undefined,
  });
  return {
    rows: res.data.data ?? [],
    dateFilter: res.data.meta?.dateFilter ?? dateFilter ?? '3M',
  };
}
